import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * MonthPicker — pemilih bulan & tahun moden (ganti <input type="month"> native
 * yang popup-nya primitif dan tidak boleh digayakan).
 *
 * Props:
 *   value: "YYYY-MM" | ""   (format sama seperti input type="month")
 *   onChange: (val: "YYYY-MM" | "") => void
 *   id, className, testId: diteruskan ke butang trigger
 */
export default function MonthPicker({ value = '', onChange, id, className = '', testId }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const now = new Date();
  const parsed = /^(\d{4})-(\d{2})$/.test(value)
    ? { y: parseInt(value.slice(0, 4), 10), m: parseInt(value.slice(5, 7), 10) - 1 }
    : null;
  const [viewYear, setViewYear] = useState(parsed ? parsed.y : now.getFullYear());

  // selaraskan tahun paparan bila value berubah dari luar
  useEffect(() => {
    if (parsed) setViewYear(parsed.y);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // tutup bila klik di luar / tekan Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open ]);

  const locale = i18n.language || 'ms';
  const monthShort = (mi) =>
    new Intl.DateTimeFormat(locale, { month: 'short' }).format(new Date(viewYear, mi, 1));
  const label = parsed
    ? new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(parsed.y, parsed.m, 1))
    : t('periode');

  const pick = (mi) => {
    onChange(`${viewYear}-${String(mi + 1).padStart(2, '0')}`);
    setOpen(false);
  };
  const thisMonth = () => {
    onChange(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`}>
      <button
        type="button" id={id} data-testid={testId}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog" aria-expanded={open}
        className={`h-12 w-full rounded-xl border bg-card px-4 flex items-center justify-between gap-2 text-sm transition-colors
          ${open ? 'border-brand ring-2 ring-brand/20' : 'border-border hover:border-border'}
          ${parsed ? 'text-foreground font-semibold' : 'text-muted-foreground'}`}
      >
        <span className="flex items-center gap-2.5 min-w-0">
          <CalendarDays className={`h-5 w-5 shrink-0 ${parsed ? 'text-brand' : 'text-muted-foreground'}`} />
          <span className="truncate">{label}</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div role="dialog" aria-label={t('periode')}
          className="absolute z-50 mt-2 w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl border border-border bg-card p-4 shadow-xl shadow-slate-900/10 animate-[fadeSlideIn_.15s_ease-out]">
          {/* navigasi tahun */}
          <div className="flex items-center justify-between mb-3">
            <button type="button" onClick={() => setViewYear((y) => y - 1)} aria-label={t('tahunSebelumnya')}
              className="h-9 w-9 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="text-base font-bold text-foreground tabular-nums">{viewYear}</span>
            <button type="button" onClick={() => setViewYear((y) => y + 1)} aria-label={t('tahunBerikutnya')}
              className="h-9 w-9 rounded-xl flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* grid bulan */}
          <div className="grid grid-cols-3 gap-1.5">
            {Array.from({ length: 12 }, (_, mi) => {
              const selected = parsed && parsed.y === viewYear && parsed.m === mi;
              const isCurrent = now.getFullYear() === viewYear && now.getMonth() === mi;
              return (
                <button key={mi} type="button" onClick={() => pick(mi)}
                  className={`h-11 rounded-xl text-sm font-medium transition-all
                    ${selected
                      ? 'bg-brand text-white shadow-md shadow-brand/30 scale-[1.02]'
                      : 'text-muted-foreground hover:bg-brand-light hover:text-brand'}
                    ${!selected && isCurrent ? 'ring-1 ring-inset ring-brand/50 text-brand font-semibold' : ''}`}>
                  {monthShort(mi)}
                </button>
              );
            })}
          </div>

          {/* aksi kaki */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border">
            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
              className="text-sm font-medium text-muted-foreground hover:text-muted-foreground transition-colors">
              {t('padam')}
            </button>
            <button type="button" onClick={thisMonth}
              className="text-sm font-semibold text-brand hover:text-brand-royal transition-colors">
              {t('bulanIni')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
